import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { apiUrl } from "@/lib/api-client";

/**
 * POST /api/crm/deals/:id/auto-link-documents once per deal id while this hook is mounted.
 *
 * Radix Tabs unmount inactive `TabsContent` panels, so `SuperfakturaDocumentsTab` may not
 * mount until the user opens the Superfaktura tab — then the progress bar / tab badge would
 * stay stale. Running auto-link from the deal page ensures SF documents link in the
 * background as soon as the deal detail loads (same behaviour as opening the documents tab).
 */
export function useSuperfakturaDealAutoLink(dealId: string | undefined) {
  const qc = useQueryClient();
  const lastTriggeredForId = useRef<string | null>(null);

  useEffect(() => {
    if (!dealId) return;
    if (lastTriggeredForId.current === dealId) return;
    lastTriggeredForId.current = dealId;

    fetch(apiUrl(`/api/crm/deals/${dealId}/auto-link-documents`), {
      method: "POST",
      credentials: "include",
    })
      .then((r) => {
        if (r.ok) {
          qc.invalidateQueries({ queryKey: ["deal-documents", dealId] });
        }
      })
      .catch(() => {});
  }, [dealId, qc]);
}
